import React, { useCallback, useEffect, useState } from 'react';
import { Field, FieldProps, Form, Formik, FormikState } from 'formik';
import {
    Box,
    Dialog,
    FormControl,
    FormControlProps,
    FormHelperText,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    //TextField,
    Typography,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { useSnackbar } from 'notistack';
import format from 'date-fns/format';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormikSelectControl from 'components/atoms/FormikSelect/FormicSelectControl';
import { CourierCallPost, Customer, initCourierCallPost } from 'api/types/CourierCallPost';
import { BranchDto, getOpenBranches } from 'api/branches';
import { fetchRegionsDict, RegionDto } from 'api/regions';
import FormikDatePicker from 'components/atoms/FormikDatePicker/FormikDatePicker';
import FormikTextField from 'components/atoms/FormikInput/FormikInput';
import { courierCallPost } from 'api/courier-call';
import FormikMaskedInput from 'components/atoms/FormikMaskedInput/FormikMaskedInput';
import { Props } from './props';
import { validationScheme } from './helpers';
import { useStyles } from './styles';
import AutocompleteWithAjax from '../../atoms/AutoComplete/AutoCompleteSearch';

let myMap: any;
const { ymaps } = window as any;
let myPlacemark: any;

export const CourierCallEditDialog: React.FC<Props> = ({ open, onClose }) => {
    const classes = useStyles();
    const [item, setItem] = useState<CourierCallPost>(initCourierCallPost());
    const productTypes: { code: string; name: string }[] = [
        {
            code: 'GOOD',
            name: 'Посылка',
        },
        {
            code: 'DOC',
            name: 'Пакет',
        },
    ];
    const payMethods: { code: string; name: string }[] = [
        { code: 'ONLINE', name: 'Оплата карточкой' },
        { code: 'CASH', name: 'Наличными' },
        { code: 'UNDER_CONTRACT', name: 'По договору' },
        { code: 'BY_RECIPIENT', name: 'Оплата получателем' },
    ];
    const [regions, setRegions] = useState<RegionDto[]>([]);
    const [branches, setBranches] = useState<BranchDto[]>([]);
    const [latitude, setLatitude] = useState<number | undefined>();
    const [longitude, setLongitude] = useState<number | undefined>();
    const [address, setAddress] = useState('');
    const [selectedRegion, setSelectedRegion] = useState<string>('');
    const { enqueueSnackbar } = useSnackbar();
    const [showMap, setShowMap] = useState(false);

    const handleLoad = useCallback(() => {
        const searchControl = new ymaps.control.SearchControl({
            options: {
                provider: 'yandex#map',
            },
        });

        searchControl.events.add('resultselect', (e: any) => {
            const results = searchControl.getResultsArray();
            const selectedIndex = e.get('index');

            const selected = results[selectedIndex];

            const [lat, long] = selected.geometry.getCoordinates();
            setLatitude(lat);
            setLongitude(long);
            setAddress(selected.properties.get('name'));
        });

        ymaps.ready(() => {
            myMap = new ymaps.Map('map', {
                center: [51.128207, 71.430411],
                zoom: 11,
                controls: [searchControl, 'zoomControl'],
            });

            myMap.events.add('click', (e: any) => {
                const localCoords = e.get('coords');
                insertMark(localCoords);
                // Если метка уже создана – просто передвигаем ее.

                getAddress(localCoords);
            });

            function insertMark(localCoords: any) {
                if (myPlacemark) {
                    myPlacemark.geometry.setCoordinates(localCoords);
                }
                // Если нет – создаем.
                else {
                    myPlacemark = createPlacemark(localCoords);
                    myMap.geoObjects.add(myPlacemark);
                    myPlacemark.balloon.open();
                    // Слушаем событие окончания перетаскивания на метке.
                    myPlacemark.events.add('dragend', () => {
                        getAddress(myPlacemark.geometry.getCoordinates());
                    });
                }
            }

            function getAddress(localCoords: any) {
                const roundedCoords = [
                    localCoords[0].toPrecision(6),
                    localCoords[1].toPrecision(6),
                ];
                setLatitude(localCoords[0]);
                setLongitude(localCoords[1]);

                ymaps.geocode(roundedCoords).then((res: any) => {
                    const firstGeoObject = res.geoObjects.get(0);
                    setAddress(firstGeoObject.getAddressLine());

                    myPlacemark.properties.set({
                        // Forming a string with the object's data.
                        // Specifying a string with the address of the object as the balloon content.
                        balloonContent: firstGeoObject.getAddressLine(),
                    });
                });
            }

            // Создание метки.
            function createPlacemark(coords: any) {
                return new ymaps.Placemark(coords, {
                    preset: 'islands#violetDotIconWithCaption',
                    draggable: true,
                });
            }
        });
    }, []);

    const load = useCallback(async () => {
        try {
            const regionList = await fetchRegionsDict();
            setRegions(regionList);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
        }
    }, [enqueueSnackbar]);

    const loadBranches = useCallback(
        async (regionCode: string) => {
            try {
                const branchList = await getOpenBranches({
                    value: null,
                    region_code: regionCode as string,
                });
                setBranches(branchList);
            } catch (e) {
                enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            }
        },
        [enqueueSnackbar]
    );

    const handleRegionChange = useCallback(
        async (event: any) => {
            setSelectedRegion(event.target.value);
            await loadBranches(event.target.value);
        },
        [loadBranches]
    );

    const handleSentRegionChange = useCallback(
        async (code: string) => {
            const region = regions.find((r) => r.code === code);
            console.log('region', region);

            if (myMap && region && region.latitude && region.longitude) {
                myMap.setCenter([region.latitude, region.longitude]);
            }
        },
        [regions]
    );

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (showMap) {
            handleLoad();
        }
    }, [handleLoad, showMap]);

    return (
        <Dialog
            open={open}
            classes={{ paper: classes.dialogPaper }}
            fullWidth={true}
            maxWidth="xl"
            onClose={() => {
                onClose(false);
                setAddress('');
                setSelectedRegion('');
            }}
            disableBackdropClick
        >
            <DialogTitle classes={{ root: classes.dialogTitle }}>Вызов курьера</DialogTitle>
            <DialogContent>
                <Box position="relative">
                    <Formik
                        enableReinitialize={true}
                        initialValues={item}
                        validationSchema={validationScheme()}
                        onSubmit={async (values, { setSubmitting, resetForm }) => {
                            const newValues = { ...values };

                            if (latitude) {
                                newValues.address_from.latitude = latitude;
                                newValues.address_from.longitude = longitude;
                            }

                            newValues.address_from.address = address;

                            try {
                                await courierCallPost({
                                    ...newValues,
                                    customers: [newValues.customers as Customer],
                                    courier_arrived_date: newValues.courier_arrived_date
                                        ? format(
                                              newValues.courier_arrived_date as Date,
                                              'dd.MM.yyyy'
                                          )
                                        : null,
                                });

                                enqueueSnackbar('Вызов успешно отправлен', { variant: 'success' });
                                setItem(initCourierCallPost());
                                resetForm(
                                    initCourierCallPost() as Partial<FormikState<CourierCallPost>>
                                );
                                setAddress('');
                                setSelectedRegion('');
                                onClose(true);
                            } catch (e) {
                                enqueueSnackbar(
                                    `Ошибка при сохранении данных: ${
                                        e.response?.data?.message || e
                                    }`,
                                    {
                                        variant: 'error',
                                    }
                                );
                            }

                            setTimeout(() => {
                                setSubmitting(false);
                            }, 200);
                        }}
                    >
                        {({ submitForm, isSubmitting }) => (
                            <Form className={classes.root}>
                                <Grid container xs={12} spacing={2}>
                                    <Grid item xs>
                                        <Field name="product_type">
                                            {({ ...props }) => (
                                                <FormikSelectControl
                                                    cls={classes.mt5}
                                                    label="Тип товара"
                                                    labelId="productType"
                                                    {...props}
                                                >
                                                    {productTypes.map((pt, index) => (
                                                        <MenuItem key={index} value={pt.code}>
                                                            {pt.name}
                                                        </MenuItem>
                                                    ))}
                                                </FormikSelectControl>
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs>
                                        <Field name="courier_arrived_date">
                                            {({ ...props }: FieldProps) => (
                                                <FormikDatePicker
                                                    className={classes.mt5}
                                                    label="Дата прибытия курьера"
                                                    inputVariant="standard"
                                                    clearable={true}
                                                    disableToolbar={false}
                                                    {...props}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            className={classes.mt5}
                                            component={FormikTextField}
                                            type="number"
                                            InputProps={{
                                                inputProps: { min: 0, step: 0.1, max: 1000 },
                                                endAdornment: (
                                                    <InputAdornment position="start">
                                                        кг
                                                    </InputAdornment>
                                                ),
                                            }}
                                            name="total_weight"
                                            label="Общий вес товаров"
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container xs={12} spacing={2}>
                                    <Grid item xs={4}>
                                        <Field name="payment_type">
                                            {({ ...props }) => (
                                                <FormikSelectControl
                                                    cls={classes.mt5}
                                                    label="Способ оплаты"
                                                    labelId="payment_type"
                                                    {...props}
                                                >
                                                    {payMethods.map((pm, index) => (
                                                        <MenuItem key={index} value={pm.code}>
                                                            {pm.name}
                                                        </MenuItem>
                                                    ))}
                                                </FormikSelectControl>
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Field
                                            className={classes.mt5}
                                            component={FormikTextField}
                                            type="number"
                                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                                            name="item_count"
                                            label="количество РПО в заявке"
                                        />
                                    </Grid>
                                </Grid>

                                <Box mt={4}>
                                    <Typography variant="h4">Отправитель</Typography>
                                </Box>
                                <Grid container spacing={4}>
                                    <Grid item xs={4}>
                                        <Field name="address_from.region_code">
                                            {({
                                                field,
                                                form,
                                                meta,
                                                onChange,
                                                ...props
                                            }: FieldProps &
                                                React.PropsWithChildren<any> &
                                                FormControlProps) => {
                                                const showError = meta.touched && meta.error;
                                                return (
                                                    <FormControl className={classes.mt5}>
                                                        <InputLabel
                                                            id="address_from.region_code"
                                                            error={showError}
                                                        >
                                                            Регион
                                                        </InputLabel>
                                                        <Select
                                                            labelId="address_from.region_code"
                                                            error={showError}
                                                            variant={props.variant}
                                                            value={field.value}
                                                            onChange={(e: any) => {
                                                                form.setFieldValue(
                                                                    'address_from.region_code',
                                                                    e.target.value
                                                                );

                                                                handleSentRegionChange(
                                                                    e.target.value
                                                                );
                                                            }}
                                                        >
                                                            {regions.map((region, index) => (
                                                                <MenuItem
                                                                    key={index}
                                                                    value={region.code}
                                                                >
                                                                    {region.description}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {showError && (
                                                            <FormHelperText error={!!showError}>
                                                                {meta.error}
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                );
                                            }}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={8}>

                                        <AutocompleteWithAjax setVariable={setAddress} 
                                                              url="/post-address/getAddresses?address=" 
                                                              keyItem="address_rus" 
                                                              label="Адрес"/>

                                       { /*<TextField
                                            className={classes.mt5}
                                            value={address}
                                            onChange={(e) => {
                                                setAddress(e.target.value);
                                            }}
                                            label="Адрес"
                                        />*/}

                                        <Link
                                            className={classes.showMapButton}
                                            variant="body2"
                                            onClick={() => setShowMap(!showMap)}
                                        >
                                            {showMap ? 'Скрыть карту' : 'Показать карту'}
                                        </Link>
                                    </Grid>
                                </Grid>
                                {showMap && (
                                    <Grid container>
                                        <Grid item xs>
                                            <div id="map" className={classes.mapContainer}></div>
                                        </Grid>
                                    </Grid>
                                )}
                                <Grid container spacing={4}>
                                    <Grid item xs>
                                        <Field
                                            className={classes.mt5}
                                            component={FormikTextField}
                                            name="customers.name"
                                            label="Наименование организации"
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            className={classes.mt5}
                                            component={FormikMaskedInput}
                                            name="customers.phone_number"
                                            label="Номер телефона"
                                            mask="9999999999"
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            className={classes.mt5}
                                            component={FormikTextField}
                                            name="address_from.comment"
                                            label="Комментарий"
                                        />
                                    </Grid>
                                </Grid>

                                <Box mt={4}>
                                    <Typography variant="h4">Получатель</Typography>
                                </Box>

                                <Grid container spacing={4}>
                                    <Grid item xs={4}>
                                        <FormControl className={classes.mt5}>
                                            <InputLabel id={'region'}>Регион</InputLabel>
                                            <Select
                                                labelId={'region'}
                                                onChange={handleRegionChange}
                                                value={selectedRegion}
                                            >
                                                {regions.map((region, index) => (
                                                    <MenuItem key={index} value={region.code}>
                                                        {region.description}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs>
                                        <Field name="branch_code">
                                            {({ ...props }) => (
                                                <FormikSelectControl
                                                    cls={classes.mt5}
                                                    label="Филиал"
                                                    labelId="branch_code"
                                                    {...props}
                                                >
                                                    {branches.map((branch, index) => (
                                                        <MenuItem key={index} value={branch.code}>
                                                            {branch.title}
                                                        </MenuItem>
                                                    ))}
                                                </FormikSelectControl>
                                            )}
                                        </Field>
                                    </Grid>
                                </Grid>

                                <DialogActions classes={{ root: classes.dialogActions }}>
                                    <Grid item container xs={12} justify="center" spacing={2}>
                                        <Button
                                            disabled={isSubmitting}
                                            onClick={submitForm}
                                            variant="contained"
                                            color="primary"
                                        >
                                            Сохранить
                                        </Button>
                                        <Button
                                            className={classes.cancelButton}
                                            onClick={() => onClose(false)}
                                            variant="contained"
                                        >
                                            Отмена
                                        </Button>
                                    </Grid>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
